/***************************************************************************
 * ========================================================================
 * Copyright (c) 2026 Broadcom Inc. and/or its subsidiaries. All Rights Reserved. Broadcom Confidential.
 * ========================================================================
 */

/**
 * @module HttpModule
 */

/* eslint-disable max-classes-per-file, class-methods-use-this */

import {
    HttpEvent,
    HttpHandler,
    HttpInterceptor,
    HttpParameterCodec,
    HttpParams,
    HttpRequest,
} from '@angular/common/http';

import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';

/**
 * Ensures all special characters are encoded. The default Angular encoder/decoder encodes only a
 * specific set of characters based on RFC3986.
 */
class CustomHttpParamEncoder implements HttpParameterCodec {
    public encodeKey(key: string): string {
        return encodeURIComponent(key);
    }

    public encodeValue(value: string): string {
        return encodeURIComponent(value);
    }

    public decodeKey(key: string): string {
        return decodeURIComponent(key);
    }

    public decodeValue(value: string): string {
        return decodeURIComponent(value);
    }
}

/**
 * @description
 *     HTTP Interceptor to encode QueryParams in HttpRequest. Encodes params in both URL and the
 *     params object.
 * @author Aravindh Nagrajan, alextsg
 */
@Injectable()
export class EncodeQueryParamsInterceptor implements HttpInterceptor {
    /** @override */
    public intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
        const { params: existingParams, url: originalUrl } = req;
        const urlParts = originalUrl.split('?');
        let newParams: HttpParams;

        if (existingParams instanceof HttpParams) {
            newParams = new HttpParams({
                fromString: existingParams.toString(),
            });
        } else if (typeof existingParams === 'object') {
            newParams = new HttpParams({
                fromObject: existingParams,
            });
        }

        // Params have been added as part of URL string.
        if (urlParts.length === 2) {
            const params = urlParts[1].split('&');

            params.forEach(param => {
                const [key, value] = param.split('=');

                newParams = newParams.append(key, value);
            });
        }

        const clonedRequest = req.clone({
            url: urlParts[0],
            params: new HttpParams({
                fromString: newParams.toString(),
                encoder: new CustomHttpParamEncoder(),
            }),
        });

        return next.handle(clonedRequest);
    }
}
